import { regionStore } from '../store/jotai/store';
import { coordsAtom, CoordsState } from '../store/jotai/atoms/coords.atom';
import { strapiClient } from '../core/api/strapi-client';
import { Logger } from '../core/logger/Logger';

const STORAGE_KEY = 'rin-coords';

function persistCoords(value: CoordsState) {
  regionStore.set(coordsAtom, value);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
}

let watchId: number | null = null;

function distance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  if (lat1 === 0 && lon1 === 0) {
    return Infinity;
  }
  const deg2rad = (deg: number) => deg * (Math.PI / 180);
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function updateLocation(latitude: number, longitude: number, custom: boolean | string) {
  const current = regionStore.get(coordsAtom);

  // Don't overwrite a custom location with a browser-detected one
  if (current.custom && !custom) {
    return;
  }

  persistCoords({
    ...current,
    latitude: parseFloat(latitude.toFixed(6)),
    longitude: parseFloat(longitude.toFixed(6)),
    custom,
  });
}

function reset() {
  const current = regionStore.get(coordsAtom);
  persistCoords({ ...current, custom: false });
}

async function resolveGeoAddress() {
  const coords = regionStore.get(coordsAtom);

  if (coords && coords.latitude !== 0) {
    try {
      const response: any = await strapiClient.geocodeFromLatLng({
        lat: coords.latitude,
        lng: coords.longitude,
      });

      const results = response?.data;

      if (results && results[0]) {
        if (coords.custom && typeof coords.custom === 'string') {
          const result = results.find((item: any) =>
            item.formatted_address?.includes(coords.custom)
          );

          setGeocodeAddress(typeof result === 'undefined' ? results[0] : result);
        } else {
          setGeocodeAddress(results[0]);
        }
      }
    } catch (error) {
      Logger.error(error);
    }
  }
}

function setGeocodeAddress(address: any) {
  const current = regionStore.get(coordsAtom);
  persistCoords({ ...current, geocodeAddress: address });
}

function startWatching() {
  if (watchId !== null || !navigator.geolocation) {
    return;
  }

  watchId = navigator.geolocation.watchPosition(
    (position) => {
      const coords = regionStore.get(coordsAtom);
      const dist = distance(
        position.coords.latitude,
        position.coords.longitude,
        coords.latitude,
        coords.longitude
      );

      if (dist > 1 && !coords.custom) {
        persistCoords({
          ...coords,
          latitude: parseFloat(position.coords.latitude.toFixed(6)),
          longitude: parseFloat(position.coords.longitude.toFixed(6)),
          custom: false,
        });
        resolveGeoAddress();
      }
    },
    (error) => {
      Logger.error(error);
    },
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
  );
}

function stopWatching() {
  if (watchId !== null) {
    navigator.geolocation.clearWatch(watchId);
    watchId = null;
  }
}

function get(): CoordsState {
  return regionStore.get(coordsAtom);
}

export const geolocationService = {
  updateLocation,
  reset,
  resolveGeoAddress,
  setGeocodeAddress,
  startWatching,
  stopWatching,
  get,
};
